// components/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { RBACService, PERMISSIONS, matchesRequiredRole } from '../utils/rbac';
import { getLoginRedirectUrl } from '../utils/authRedirect';
import { sanitizeAuthToken } from '../services/apiService';

interface ProtectedRouteProps {
  children: React.ReactElement;
  requiredPermissions?: string[];
  requireAll?: boolean; // If true, user must have ALL permissions. If false, user needs ANY permission
  requiredRole?: string; // If set, user's role must match (case-insensitive), e.g. "Super Admin"
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermissions = [],
  requireAll = false,
  requiredRole,
}) => {
  const location = useLocation();
  const loginUrl = getLoginRedirectUrl(location.pathname);
  const isDebugMode = process.env.REACT_APP_DEBUG === 'true';
  let isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const rawToken = localStorage.getItem('authToken');
  let token = sanitizeAuthToken(rawToken);

  // Stored value exists but is not a usable JWT (e.g. debug-token, "undefined", corrupted quotes).
  if (rawToken && !token) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    RBACService.clearUser();
    return <Navigate to={loginUrl} replace state={{ error: 'Invalid session token. Please sign in again.' }} />;
  }

  // Clear debug tokens if not in debug mode
  if (!isDebugMode && rawToken === 'debug-token') {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userRoleId');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    RBACService.clearUser();
    return <Navigate to={loginUrl} replace />;
  }

  // Debug mode: do not inject fake JWTs — API calls require a real login token.
  
  // Check if user is authenticated
  if (!isAuthenticated || !token) {
    return <Navigate to={loginUrl} replace />;
  }

  // Get current user from RBAC service
  const currentUser = RBACService.getCurrentUser();
  
  // If no user loaded, redirect to login
  if (!currentUser) {
    // Try to load user from token if available
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    return <Navigate to={loginUrl} replace />;
  }

  // If a specific role is required (e.g. Super Admin), redirect non-matching users
  if (requiredRole) {
    const roleMatch = matchesRequiredRole(
      requiredRole,
      currentUser.roleName,
      currentUser.roleId,
    );
    if (!roleMatch) {
      // Send to appropriate dashboard by role
      const r = currentUser.roleName?.toLowerCase();
      const adminPortalRoles = ['admin', 'enterprise manager'];
      if (adminPortalRoles.some(ar => r?.includes(ar))) {
        return <Navigate to="/admin-portal/dashboard" replace state={{ error: 'Super Admin access required' }} />;
      }
      return <Navigate to="/dashboard" replace state={{ error: 'You do not have permission to access this page' }} />;
    }
  }

  // If no specific permissions required, just check authentication
  if (requiredPermissions.length === 0) {
    return children;
  }

  // Check permissions
  const hasPermission = requireAll
    ? RBACService.hasAllPermissions(requiredPermissions)
    : RBACService.hasAnyPermission(requiredPermissions);

  if (!hasPermission) {
    // Redirect to dashboard with error message
    return <Navigate to="/dashboard" replace state={{ error: 'You do not have permission to access this page' }} />;
  }

  return children;
};

export default ProtectedRoute;